import React from 'react';
import { CheckCircle2, AlertTriangle, Target, Lightbulb } from 'lucide-react';
import type { ExplosiveProfile } from '@/lib/scoring';

const FOCUS_COLOR = { engine: '#8B0000', ignition: '#E10600', bounce: '#B3B3B3', balanced: '#FF3B30' };

export default function InterpretationPanel({ profile }: { profile: ExplosiveProfile }) {
  const color = FOCUS_COLOR[profile.programFocus];

  return (
    <div className="card p-5 h-full space-y-5">
      <h3 className="section-header flex items-center gap-2">
        <Lightbulb size={16} style={{ color: '#E10600' }} />
        Profile Interpretation
      </h3>

      {/* Main interpretation */}
      <div className="p-4 rounded-xl bg-zinc-800/60 border border-zinc-700/50">
        <p className="text-sm text-zinc-300 leading-relaxed">{profile.interpretation}</p>
      </div>

      {/* Strengths */}
      <div>
        <h4 className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
          <CheckCircle2 size={12} className="text-emerald-400" />
          Top Strengths
        </h4>
        <div className="space-y-2">
          {profile.strengths.map((s, i) => (
            <div key={`str-${i}`} className="flex items-start gap-3 p-3 rounded-lg bg-emerald-400/5 border border-emerald-400/15">
              <span className="w-5 h-5 rounded-full bg-emerald-400/20 text-emerald-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                {i + 1}
              </span>
              <span className="text-sm text-zinc-300">{s}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Limiters */}
      <div>
        <h4 className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
          <AlertTriangle size={12} className="text-red-400" />
          Primary Limiters
        </h4>
        <div className="space-y-2">
          {profile.limiters.map((l, i) => (
            <div key={`lim-${i}`} className="flex items-start gap-3 p-3 rounded-lg bg-red-400/5 border border-red-400/15">
              <span className="w-5 h-5 rounded-full bg-red-400/20 text-red-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                {i + 1}
              </span>
              <span className="text-sm text-zinc-300">{l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Training Priority */}
      <div className="p-4 rounded-xl border" style={{ backgroundColor: `${color}08`, borderColor: `${color}25` }}>
        <h4 className="text-xs font-medium uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color }}>
          <Target size={12} />
          Primary Training Priority
        </h4>
        <p className="text-sm font-semibold text-zinc-200">{profile.primaryPriority}</p>
      </div>
    </div>
  );
}